import Experience from '../Experience'
import Sky from './Sky'
import { parseBsp } from '@/cs/bsp'
import { Wad } from '@/cs/wad'
import { TextureStore } from '@/cs/TextureStore'
import { buildBspMesh, type BuiltMap } from '@/cs/buildBspMesh'

const DEFAULT_MAP = 'fy_pool_day.bsp'
const DEFAULT_WADS = ['halflife.wad', 'decals.wad', 'cs_dust.wad']

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to load ${url} (${res.status})`)
  return res.arrayBuffer()
}

function normalizeMapName(raw: string | undefined): string {
  const name = (raw ?? '').trim() || DEFAULT_MAP
  const base = name.split(/[\\/]/).pop() || DEFAULT_MAP
  return base.toLowerCase().endsWith('.bsp') ? base : `${base}.bsp`
}

async function fetchWads(names: string[]): Promise<Wad[]> {
  const unique = [...new Set(names.map((n) => n.toLowerCase()))]
  const loaded = await Promise.all(
    unique.map(async (name): Promise<Wad | null> => {
      try {
        return new Wad(await fetchArrayBuffer(`/cs/wads/${name}`))
      } catch (error) {
        console.warn(`[cs] WAD "${name}" failed to load:`, error)
        return null
      }
    }),
  )
  return loaded.filter((wad): wad is Wad => wad !== null)
}

/** Loads, parses and renders a GoldSrc BSP map into the scene. */
export default class CsMap {
  private readonly experience = Experience.instance
  private readonly mapName = normalizeMapName(import.meta.env.CS_MAP)
  public built?: BuiltMap
  private sky?: Sky

  constructor() {
    void this.load()
  }

  private async load(): Promise<void> {
    try {
      const bspBuf = await fetchArrayBuffer(`/cs/${this.mapName}`)
      const bsp = parseBsp(bspBuf)
      const wads = await fetchWads([...DEFAULT_WADS, ...bsp.wads])
      const store = new TextureStore(bsp, wads)
      const built = buildBspMesh(bsp, store)
      this.built = built

      this.experience.scene.add(built.object)

      // Original GoldSrc skybox (worldspawn "skyname", e.g. "des" for de_dust2).
      this.sky = new Sky(bsp.entities[0]?.skyname ?? 'des')

      const spawn = built.spawns[0]
      if (spawn) {
        // spawn.position is the feet; lift to eye height for the pre-game view.
        const eye = spawn.position.clone()
        eye.y += 56
        this.experience.camera.placeAt(eye, spawn.angle)
      }

      this.reveal()
      this.setDebug()

      console.info(
        `[cs] ${this.mapName} loaded — ${built.stats.rendered}/${built.stats.faces} faces, ` +
          `${built.stats.textures} textures (${built.stats.missing} missing), ` +
          `lightmap ${built.lightmap.image.width}x${built.lightmap.image.height}`,
      )
    } catch (error) {
      console.error('[cs] failed to load map:', error)
      const loader = document.getElementById('loader')
      if (loader) loader.textContent = `Error: ${(error as Error).message}`
    }
  }

  private reveal(): void {
    document.getElementById('loader')?.classList.add('loader--done')
    document.getElementById('blocker')?.classList.remove('hidden')
  }

  private setDebug(): void {
    const { debug } = this.experience
    if (!debug.active || !debug.ui || !this.built) return

    const built = this.built
    const params = {
      lightmapIntensity: built.materials[0]?.lightMapIntensity ?? 2,
      wireframe: false,
    }
    const folder = debug.ui.addFolder(this.mapName)
    folder
      .add(params, 'lightmapIntensity', 0, 5, 0.05)
      .name('lightmap')
      .onChange((v: number) => {
        for (const m of built.materials) m.lightMapIntensity = v
      })
    folder.add(params, 'wireframe').onChange((v: boolean) => {
      for (const m of built.materials) m.wireframe = v
    })
  }

  public update(): void {
    this.sky?.update()
  }
}
